/* components/SiteHeader.ts
Global Site Header

Example
<site-header>
    Site header content here
</site-header>
*/

import { Component, Ref, Prop, SimpleEvent } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { cart } from "@/theme/Cart";
import { debounce } from 'lodash';
import { CartMain } from '../Cart/CartMain';
import { AnnouncementBar } from '../AnnouncementBar/AnnouncementBar';

declare global {
    interface Window {
        // eslint-disable-next-line
        _swat?: any;
    }
}
@Component
export class SiteHeader extends ThemeComponent {
    @Prop() wishlistEnabled!: boolean;
    @Ref() row!: HTMLDivElement;
    @Ref({ required: false }) cartTrigger!: HTMLDivElement;
    @Ref() menuToggle!: HTMLButtonElement;
    @Ref({required: false}) count!: HTMLElement;
    @Ref({ required: false }) announcement!: AnnouncementBar;
    @Ref({required:false}) wishlist!: HTMLElement;

    private open: string = '';
    private _onOpenChange = new SimpleEvent<string>();

    public get onOpenChange() {
        return this._onOpenChange.asEvent();
    }

    public changeOpen(open: string) {
        this.open = open;

        // Use the private backing field to dispatch
        this._onOpenChange.dispatch(open);
    }

    init() {
        this.handleBodyPadding();
        this.handleScroll();
        this.cartEvent();
        this.menuEvent();
        this.$subscribe(cart.onChange, debounce(() => {
            this.updateCartCount();
        }, 300));

        document.addEventListener('wishlistUpdated', () => {
            this.updateWishlistCount();
        });

        // Wait for Wishlist to load

        document.addEventListener('swym-loaded', () => {
            this.updateWishlistCount();
        });

    }

    /**
     * Handle Body Padding.
     */
    private handleBodyPadding() {
        let height = this.offsetHeight;
        if (height == 0) {
            height = 112;
        }
        document.body.style.paddingTop = `${height}px`;
    }

    private handleScroll() {
        const headerHeight = this.offsetHeight;
        const header = this.closest('header');
        let height = 0;
        window.addEventListener('scroll', (e) => {
            if (window.scrollY > headerHeight && window.scrollY > height) {
                header?.classList.add('site-header--down');
                header?.classList.remove('site-header--up');
                this.announcement.classList.remove('visually-hidden');
            } else if (window.scrollY > headerHeight && window.scrollY < height) {
                header?.classList.add('site-header--up');
                header?.classList.remove('site-header--down');
                this.announcement.classList.add('visually-hidden');
            } else if (window.scrollY < headerHeight) {
                header?.classList.remove('site-header--down');
                header?.classList.remove('site-header--up');
                this.announcement.classList.remove('visually-hidden');
            }
            height = window.scrollY;
        });
    }
    /**
     * Cart Close click event.
     * @returns
     */
    cartEvent() {
        if (this.cartTrigger) {
            this.$listen(this.cartTrigger, 'click', (event) => {
                event.preventDefault();
                document.dispatchEvent(new Event('cartOpen'));
            });
        }
    }

    menuEvent() {
        this.$listen(this.menuToggle, 'click', () => {
            this.handleDrawerOpen();
        });

    }

    /**
     * Update cart count
     * @returns
     */
    updateCartCount() {
        if(!this.count) { return; }

        const itemCount = cart.items.filter((item) => ~~item.quantity > 0)
            .map((obj) => ~~obj.quantity)
            .reduce((a, b) => a + b, 0);

        this.count.innerHTML = String(itemCount);

        if (itemCount > 0) {
            this.count.parentElement?.classList.add('is-active');
        } else {
            this.count.parentElement?.classList.remove('is-active');
        }
    }

    /**
     *
     * @returns Update wishlist count.
    */
    updateWishlistCount() {
        if (!this.wishlistEnabled) { return; } // wishlist disabled

        /* eslint-disable */
        // swym wishlist specific call format fails ts linting.
        if (!window._swat) { return; }
        const $this = this;

        window._swat.wishlistCount((count: string) => {
            $this.wishlist.innerHTML = count;
            if (~~count <= 0) {
                $this.wishlist.parentElement?.classList.remove('is-active');
                return;
            }
            $this.wishlist.parentElement?.classList.add('is-active');
        });
        /* eslint-enable */
    }

    /**
     * Trigger menu drawer.
     * @returns
     */
    handleDrawerOpen() {
        if (window.innerWidth > 1200) { return; }
        const menu = document.querySelector('.mobile-menu');
        const overlay = document.querySelector('.mobile-menu__overlay');
        document.documentElement.classList.add('drawer-open');
        document.body.classList.add('drawer-open');
        menu?.classList.add('is-active');
        overlay?.classList.add('is-active');
    }

}
